import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, ArrowRight, Trash2 } from 'lucide-react';
import { api } from '../lib/gapApi';

// Maps a history row's `type` (from GET /history) to the tab/route that can
// replay it via ?historyId=<id>.
const TYPE_ROUTES = {
  new_analysis: '/analyzer/analysis/new',
  general: '/analyzer/gap-analysis',
  instagram: '/analyzer/instagram-analysis',
  linkedin: '/analyzer/linkedin-analysis',
  reddit: '/analyzer/reddit-analysis',
  glassdoor: '/analyzer/glassdoor-analysis',
  g2: '/analyzer/g2-analysis',
  trustpilot: '/analyzer/trustpilot-analysis',
  twitter: '/analyzer/twitter-analysis',
};

const TYPE_LABELS = {
  new_analysis: 'New Analysis',
  general: 'General',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
  reddit: 'Reddit',
  glassdoor: 'Glassdoor',
  g2: 'G2',
  trustpilot: 'Trustpilot',
  twitter: 'Twitter',
};

// Base path for DELETE `${TYPE_DELETE_BASE[type]}/{id}`, per-type per the backend's
// /new-analysis/{id}, /gap-analysis/{id}, and /social/{platform}/{id} endpoints.
const TYPE_DELETE_BASE = {
  new_analysis: '/new-analysis',
  general: '/gap-analysis',
  instagram: '/social/instagram',
  linkedin: '/social/linkedin',
  reddit: '/social/reddit',
  glassdoor: '/social/glassdoor',
  g2: '/social/g2',
  trustpilot: '/social/trustpilot',
  twitter: '/social/twitter',
};

export const AnalysisHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const { data } = await api.get('/history?limit=50');
        setHistory(data);
      } catch (error) {
        console.error("Failed to fetch history");
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, []);

  const handleDelete = async (item) => {
    if (!window.confirm(`Delete this ${TYPE_LABELS[item.type] || item.type} analysis for "${item.company_name}"? This cannot be undone.`)) return;
    const base = TYPE_DELETE_BASE[item.type];
    if (!base) {
      alert(`Unknown analysis type "${item.type}" — cannot delete.`);
      return;
    }
    setDeletingId(item.id);
    try {
      await api.delete(`${base}/${item.id}`);
      setHistory((prev) => prev.filter((h) => h.id !== item.id));
    } catch (err) {
      console.error('Failed to delete analysis', err);
      alert('Failed to delete analysis: ' + (err.response?.data?.detail || err.message));
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex justify-center items-center h-[80vh]">
        <Loader2 className="animate-spin text-blue-500 w-12 h-12" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold mb-8 text-white">Analysis History</h1>

      {history.length === 0 ? (
        <div className="bg-slate-800 p-12 rounded-2xl border border-slate-700 text-center">
          <p className="text-slate-400 mb-4 text-lg">No analysis history found.</p>
          <Link to="/analyzer/analysis/new" className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300">
            <span>Start a new analysis</span>
            <ArrowRight size={16} />
          </Link>
        </div>
      ) : (
        <div className="bg-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-slate-900/50 border-b border-slate-700">
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Company</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Type</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Website</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400">Date</th>
                <th className="px-6 py-4 text-sm font-medium text-slate-400 text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {history.map((item, idx) => (
                <tr key={item.id || idx} className="hover:bg-slate-700/50 transition-colors">
                  <td className="px-6 py-4 font-medium text-white">{item.company_name}</td>
                  <td className="px-6 py-4">
                    <span className="text-xs font-bold uppercase tracking-wider text-slate-300 bg-slate-700/50 px-2.5 py-1 rounded-full border border-slate-600/50">
                      {TYPE_LABELS[item.type] || item.type}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-slate-400">{item.website || 'N/A'}</td>
                  <td className="px-6 py-4 text-slate-400">
                    {new Date(item.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`${TYPE_ROUTES[item.type] || '/analyzer/history'}?historyId=${item.id}`}
                        className="text-blue-400 hover:text-blue-300 flex items-center gap-1 font-medium bg-blue-500/10 border border-blue-500/20 px-3 py-1 rounded-md transition-colors inline-flex"
                      >
                        View Results <ArrowRight size={16} />
                      </Link>
                      <button
                        type="button"
                        onClick={() => handleDelete(item)}
                        disabled={deletingId === item.id}
                        aria-label={`Delete analysis for ${item.company_name}`}
                        title="Delete"
                        className="text-rose-400 hover:text-rose-300 bg-rose-500/10 border border-rose-500/20 p-1.5 rounded-md transition-colors disabled:opacity-50"
                      >
                        {deletingId === item.id ? <Loader2 size={16} className="animate-spin" /> : <Trash2 size={16} />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AnalysisHistory;
